import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Ban, BadgeCheck, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/assets";

export default function WithdrawNomination() {
  const { token } = useParams();
  const [nom, setNom] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/nominations/withdraw/${token}`)
      .then(({ data }) => setNom(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "This link is not valid"));
  }, [token]);

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.post(`/nominations/withdraw/${token}`);
      setDone(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="withdraw-page" className="min-h-screen flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md border border-clay-border bg-clay-card p-8 sm:p-10">
        <img src={LOGO_URL} alt="Mid Western Speedway Darlington" className="h-16 mb-6" />
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay-orange mb-2">Mid Western Speedway Darlington</p>

        {error ? (
          <div data-testid="withdraw-error">
            <h1 className="font-display font-bold uppercase text-2xl tracking-tight mb-3">Link not valid</h1>
            <p className="text-clay-muted text-sm leading-relaxed">
              This withdrawal link is not valid. Reply to your nomination email or contact the club and we'll sort it out.
            </p>
          </div>
        ) : done || nom?.withdrawn ? (
          <div data-testid="withdraw-done" className="text-center py-6">
            <BadgeCheck className="w-14 h-14 text-clay-gold mx-auto mb-4" />
            <h1 className="font-display font-bold uppercase text-2xl tracking-tight mb-3">Nomination withdrawn</h1>
            <p className="text-clay-muted text-sm leading-relaxed">
              All done{nom ? `, ${nom.driver_name?.split(" ")[0]}` : ""} — you're withdrawn from {nom?.event_title || "the meeting"}.
              The club has been notified. Changed your mind? Just reply to your nomination email and we'll reinstate you.
            </p>
          </div>
        ) : nom ? (
          <>
            <h1 className="font-display font-bold uppercase text-2xl tracking-tight mb-1">Withdraw Nomination?</h1>
            <p className="text-clay-muted text-sm mb-6">
              {nom.driver_name} — Car #{nom.car_number || "—"} ({nom.racing_class || "class TBC"}) at {nom.event_title}, {nom.event_date}.
            </p>
            <p className="text-clay-muted text-sm mb-6 leading-relaxed">
              You'll stay on the nominations list crossed out, so the club can reinstate you if plans change.
            </p>
            <button
              data-testid="withdraw-confirm-button"
              onClick={withdraw}
              disabled={busy}
              className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-bold uppercase tracking-wider px-6 py-4 transition-colors flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
              {busy ? "Withdrawing…" : "Yes, Withdraw My Nomination"}
            </button>
          </>
        ) : (
          <div className="flex justify-center py-10">
            <Loader2 className="w-8 h-8 animate-spin text-clay-orange" />
          </div>
        )}
      </div>
    </main>
  );
}
